import React, { createContext, useCallback } from 'react';

import * as Fullstory from '@fullstory/browser';
import { useLocalStorageValue } from '@react-hookz/web';

import { useCurrentUser } from '@lib/hooks';

import { NEXTY_BANNER_STORAGE_KEY, NEXTY_NEXT_VERSION_STORAGE_KEY } from '../constants';

interface INextyContextValues {
  nextyStorageValue: boolean;
  toggleNextyNextStorage: () => void;
  shouldHideNextyBanner: boolean | null;
  hideNextyNextBanner: () => void;
}

function useNextyNextValues(): INextyContextValues {
  const currentUser = useCurrentUser();
  const nextyBannerStorageKey = NEXTY_BANNER_STORAGE_KEY(currentUser?.id);
  const [shouldHideNextyBanner, updateNextyBannerStorageValue] = useLocalStorageValue<boolean>(nextyBannerStorageKey);
  const hideNextyNextBanner = useCallback(() => updateNextyBannerStorageValue(true), [updateNextyBannerStorageValue]);

  const nextyStorageKey = NEXTY_NEXT_VERSION_STORAGE_KEY(currentUser?.id);
  const [nextyStorageValue, updateNextyStorageValue] = useLocalStorageValue<boolean>(nextyStorageKey, true);

  const toggleNextyNextStorage = useCallback(() => {
    updateNextyStorageValue(!nextyStorageValue);

    // We want to know if the nexty-next users has came back to the non-nexty UI after being on nexty
    Fullstory.setUserVars({
      isNextyEnabled: !nextyStorageValue,
      ...(nextyStorageValue ? { hasCameBackToNonNextyUI: true } : {}),
    });
  }, [nextyStorageValue, updateNextyStorageValue]);

  return {
    nextyStorageValue,
    toggleNextyNextStorage,
    shouldHideNextyBanner,
    hideNextyNextBanner,
  };
}

export const NextyContext = createContext<INextyContextValues | undefined>(undefined);

const NextyProvider: React.FC = ({ children }) => {
  const nextyNextyValues = useNextyNextValues();

  return <NextyContext.Provider value={nextyNextyValues}>{children}</NextyContext.Provider>;
};

export default NextyProvider;
